import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { Contract } from '../types';
import { Loader2, ArrowRight, AlertCircle, FileText, Send } from 'lucide-react';
// import { getContractorProfile } from '../services/contractors'; // Not used yet

// Define Status Columns matching SavedContracts.tsx
const COLUMNS = [
    { id: 'new', title: 'New Leads', color: 'blue', icon: AlertCircle },
    { id: 'review', title: 'Under Review', color: 'yellow', icon: FileText },
    { id: 'bid', title: 'Bidding', color: 'purple', icon: ArrowRight },
    { id: 'submitted', title: 'Submitted', color: 'green', icon: Send },
];

interface KanbanCardProps {
    contract: Contract;
    onStatusChange: (contractId: string, newStatus: string) => void;
}

function KanbanCard({ contract, onStatusChange }: KanbanCardProps) {
    return (
        <div
            draggable
            onDragStart={(e) => {
                e.dataTransfer.setData('contractId', contract.id);
            }}
            className="group relative bg-secondary/40 backdrop-blur-sm border border-white/5 rounded-xl p-4 hover:bg-secondary/60 hover:border-blue-500/30 transition-all cursor-grab active:cursor-grabbing shadow-lg mb-3"
        >
            <div className="flex justify-between items-start mb-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${contract.type === 'Solicitation' ? 'bg-blue-500/10 text-blue-400 border-blue-500/20' :
                    'bg-purple-500/10 text-purple-400 border-purple-500/20'
                    }`}>
                    {contract.type || 'Opportunity'}
                </span>
                {contract.fitScore && (
                    <span className="text-xs font-bold text-green-400">
                        {contract.fitScore}% Match
                    </span>
                )}
            </div>

            <h4 className="text-sm font-semibold text-white mb-1 line-clamp-2 leading-snug">
                {contract.title}
            </h4>
            <p className="text-xs text-secondary mb-3 truncate">
                {contract.agency || 'Unknown Agency'}
            </p>

            {/* Quick Actions (Hover) */}
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity absolute top-2 right-2 bg-black/80 rounded-lg p-1 border border-white/10">
                {/* Simplified quick move for non-drag users */}
                <select
                    value=""
                    onChange={(e) => onStatusChange(contract.id, e.target.value)}
                    className="bg-transparent text-[10px] text-white outline-none cursor-pointer w-4"
                >
                    <option value="" disabled className="bg-gray-900 text-gray-500">Move...</option>
                    {COLUMNS.map(col => (
                        <option key={col.id} value={col.id} className="bg-gray-900 text-white">{col.title}</option>
                    ))}
                </select>
            </div>

            <div className="flex items-center justify-between mt-2 pt-2 border-t border-white/5">
                <span className="text-[10px] text-tertiary">
                    {contract.postedDate ? new Date(contract.postedDate).toLocaleDateString() : 'No Date'}
                </span>
                <span className="text-[10px] font-mono text-tertiary">
                    {contract.solicitationNumber || 'N/A'}
                </span>
            </div>
        </div>
    );
}

interface PipelineBoardProps {
    contractorId?: string;
}

export default function PipelineBoard({ contractorId }: PipelineBoardProps) {
    const { appUser } = useAuth();
    const [contracts, setContracts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [dragOverCol, setDragOverCol] = useState<string | null>(null);

    // Use passed prop or fall back to auth user
    const targetId = contractorId || appUser?.contractorId;

    // Load Saved Opportunities
    useEffect(() => {
        if (!targetId) {
            setLoading(false);
            return;
        }

        // Corrected Query: Use 'savedContracts' collection
        const q = query(
            collection(db, 'savedContracts'),
            where('contractorId', '==', targetId)
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(doc => ({
                id: doc.id.split('_')[1] || doc.id, // Extract actual noticeId if composite, or just use ID. 
                // In favorites.ts we save as `${contractorId}_${noticeId}`.
                // But the data() inside has 'id' field which IS noticeId. 
                // Let's rely on data().id if available, fallback to doc.id.
                ...doc.data(),
                status: doc.data().status || 'new' // Default to 'new' to match Column 1
            }));

            setContracts(data);
            setLoading(false);
        }, (error) => {
            console.error("Error fetching pipeline:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [appUser]);


    const handleStatusChange = async (contractId: string, newStatus: string) => {
        if (!appUser?.contractorId) return;

        // Optimistic Update
        setContracts(prev => prev.map(c =>
            c.id === contractId ? { ...c, status: newStatus } : c
        ));

        try {
            // Reconstruct the composite ID used in favorites.ts
            const docId = `${appUser.contractorId}_${contractId}`;
            const docRef = doc(db, 'savedContracts', docId);
            await updateDoc(docRef, { status: newStatus });
        } catch (error) {
            console.error("Failed to move card:", error);
            // Revert logic could be added here
        }
    };

    const onDrop = (e: React.DragEvent, status: string) => {
        e.preventDefault();
        setDragOverCol(null);
        const contractId = e.dataTransfer.getData('contractId');
        if (contractId) {
            handleStatusChange(contractId, status);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            </div>
        );
    }

    return (
        <div className="h-[calc(100vh-140px)] overflow-x-auto overflow-y-hidden pb-4">
            <div className="flex gap-4 h-full min-w-[1000px] px-1">
                {COLUMNS.map(col => {
                    const colContracts = contracts.filter(c => (c.status || 'new') === col.id);
                    const isOver = dragOverCol === col.id;
                    const Icon = col.icon;

                    return (
                        <div
                            key={col.id}
                            onDragOver={(e) => { e.preventDefault(); setDragOverCol(col.id); }}
                            onDragLeave={() => setDragOverCol(null)}
                            onDrop={(e) => onDrop(e, col.id)}
                            className={`flex flex-col flex-1 min-w-[280px] rounded-xl transition-colors border ${isOver ? 'bg-white/5 border-blue-500/50' : 'bg-black/20 border-white/5'
                                }`}
                        >
                            {/* Header */}
                            <div className={`p-4 border-b border-white/5 flex items-center justify-between ${isOver ? 'text-blue-400' : 'text-gray-300'
                                }`}>
                                <div className="flex items-center gap-2">
                                    <Icon className={`w-4 h-4 text-${col.color}-400`} />
                                    <h3 className="font-bold text-sm tracking-wide uppercase">{col.title}</h3>
                                </div>
                                <span className="bg-white/10 text-[10px] px-2 py-0.5 rounded-full font-mono">
                                    {colContracts.length}
                                </span>
                            </div>

                            {/* Droppable Area */}
                            <div className="flex-1 overflow-y-auto p-3 custom-scrollbar space-y-3">
                                {colContracts.map(contract => (
                                    <KanbanCard
                                        key={contract.id}
                                        contract={contract}
                                        onStatusChange={handleStatusChange}
                                    />
                                ))}
                                {colContracts.length === 0 && (
                                    <div className="h-24 border-2 border-dashed border-white/5 rounded-lg flex items-center justify-center text-xs text-gray-600 italic">
                                        Drop items here
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
